import React from 'react';
import { Plus, Loader2, Sparkles, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { CATEGORIES, OUTCOME_TYPES, EXPIRY_OPTIONS } from './ideasConstants';

export const CreateIdeaModal = ({ open, onOpenChange, newIdea, setNewIdea, onSubmit, creating }) => {
  const subcategories = CATEGORIES.find(c => c.id === newIdea.category)?.subcategories || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-[#050810] border-[#1e2a45] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#F97316]" />
            Submit New Market Idea
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Share your idea for a new prediction market. Popular ideas may become live markets!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Market Question *</label>
            <Input
              value={newIdea.title}
              onChange={(e) => setNewIdea(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Will Manchester United win the Premier League?"
              className="bg-[#1e2a45] border-[#2d3a56] text-white"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
            <textarea
              value={newIdea.description}
              onChange={(e) => setNewIdea(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Explain how this market should resolve..."
              className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg resize-none h-24 focus:outline-none focus:border-[#3B82F6]"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Category *</label>
            <select
              value={newIdea.category}
              onChange={(e) => setNewIdea(prev => ({ ...prev, category: e.target.value, subcategory: '' }))}
              className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg focus:outline-none focus:border-[#3B82F6]"
            >
              {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {subcategories.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Subcategory *</label>
              <select
                value={newIdea.subcategory}
                onChange={(e) => setNewIdea(prev => ({ ...prev, subcategory: e.target.value }))}
                className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] text-white rounded-lg focus:outline-none focus:border-[#3B82F6]"
              >
                <option value="">Select subcategory...</option>
                {subcategories.map(sub => (
                  <option key={sub} value={sub}>{sub.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Outcome Type</label>
            <div className="grid grid-cols-2 gap-3">
              {OUTCOME_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setNewIdea(prev => ({ ...prev, outcomeType: type.id }))}
                  className={`p-3 rounded-xl text-left transition-all ${
                    newIdea.outcomeType === type.id
                      ? 'bg-[#3B82F6]/20 border-2 border-[#3B82F6]'
                      : 'bg-[#1e2a45] border-2 border-transparent hover:border-[#2d3a56]'
                  }`}
                >
                  <div className="font-medium text-white text-sm">{type.name}</div>
                  <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Event Expiry (When will this resolve?)
            </label>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {EXPIRY_OPTIONS.filter(o => o.id !== 'custom').map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setNewIdea(prev => ({ ...prev, expiryOption: option.id }))}
                  className={`px-3 py-2 text-sm rounded-lg transition-all ${
                    newIdea.expiryOption === option.id
                      ? 'bg-[#F97316] text-white'
                      : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                  }`}
                >
                  {option.name}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setNewIdea(prev => ({ ...prev, expiryOption: 'custom' }))}
              className={`w-full px-3 py-2 text-sm rounded-lg transition-all ${
                newIdea.expiryOption === 'custom'
                  ? 'bg-[#F97316] text-white'
                  : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
              }`}
            >
              Custom Date
            </button>
            {newIdea.expiryOption === 'custom' && (
              <Input
                type="date"
                value={newIdea.customDate}
                onChange={(e) => setNewIdea(prev => ({ ...prev, customDate: e.target.value }))}
                className="mt-2 bg-[#1e2a45] border-[#2d3a56] text-white"
                min={new Date().toISOString().split('T')[0]}
              />
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white rounded-full"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || !newIdea.title.trim() || !newIdea.description.trim()}
              className="flex-1 bg-gradient-to-b from-[#F97316] to-[#EA580C] hover:from-[#FB923C] hover:to-[#F97316] text-white rounded-full"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Submit Idea
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
